import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { m } from "framer-motion";
import { Eye, EyeOff, Fingerprint, Loader2, Lock, Shield } from "lucide-react";

import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { unlockWithBiometric } from "@/lib/webauthn-client";

interface PinGateModalProps {
  open: boolean;
  onUnlock: () => void;
  onCancel: () => void;
  pinLength: number;
  hasBiometric: boolean;
}

function PinGateForm({
  onUnlock,
  onCancel,
  pinLength,
  hasBiometric,
}: Omit<PinGateModalProps, "open">) {
  const { t } = useLingui();
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  /** Why the last PIN was refused: wrong, or the PIN-guess budget is spent (429). */
  const [error, setError] = useState<"wrong" | "limited" | null>(null);
  /**
   * How long PIN entry is paused (seconds, from the 429). Wrong PINs pile up
   * into bans of 5 min → 30 min → 2 h → 24 h on the server, so "a few
   * minutes" may be a day.
   */
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [verifying, setVerifying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus after render frame so the dialog focus-trap has settled. Using
  // setTimeout inside the first render caused Firefox to hang because
  // the input was still being mounted when autoFocus ran.
  useEffect(() => {
    const handle = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(handle);
  }, []);

  // A right PIN makes the server write the unlock (pm_vault) the vault
  // endpoints check; onUnlock only opens the content.
  const verifyPin = useCallback(
    async (pinValue: string) => {
      setError(null);
      setVerifying(true);
      try {
        await apiFetch.post("/api/account/vault-security/verify", { pin: pinValue });
        onUnlock();
      } catch (err) {
        const limited = err instanceof ApiError && err.status === 429;
        setError(limited ? "limited" : "wrong");
        const wait = limited ? (err.body as { retryAfter?: unknown } | null)?.retryAfter : null;
        setRetryAfter(typeof wait === "number" && wait > 0 ? wait : null);
        setPin("");
        setVerifying(false);
        // Restore focus on next frame — setting focus while React is still
        // committing the enabled-state change is a no-op in Firefox.
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    },
    [onUnlock],
  );

  const handleChange = useCallback(
    (value: string) => {
      const v = value.replace(/\D/g, "");
      setPin(v);
      setError(null);
      if (pinLength > 0 && v.length === pinLength) {
        verifyPin(v);
      } else if (pinLength === 0 && v.length >= 4) {
        // Unknown length — try at each valid length (4, 5, 6)
        verifyPin(v);
      }
    },
    [pinLength, verifyPin],
  );

  const [biometricBusy, setBiometricBusy] = useState(false);

  const handleBiometric = useCallback(async () => {
    if (biometricBusy) return;
    setBiometricBusy(true);
    try {
      const ok = await unlockWithBiometric();
      if (ok) onUnlock();
    } catch (err) {
      // Server / network error — fall back to PIN silently.
      void err;
    } finally {
      setBiometricBusy(false);
    }
  }, [biometricBusy, onUnlock]);

  return (
    <div className="space-y-6 py-2 text-center">
      <div className="bg-primary/10 mx-auto flex size-16 items-center justify-center rounded-full">
        <Shield className="text-primary size-8" />
      </div>

      <div>
        <h2 className="text-foreground text-lg font-bold">
          <Trans>Protected content</Trans>
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Unlock your medical vault to continue.</Trans>
        </p>
      </div>

      <div className="space-y-3">
        <div className="relative">
          <Lock className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="one-time-code"
            // Firefox-safe: readOnly keeps the element focusable during verify,
            // so the focus-trap in the Dialog doesn't try to relocate focus
            // to another element (which was stalling the UI).
            readOnly={verifying}
            value={pin}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={t`Enter PIN`}
            style={showPin ? undefined : ({ WebkitTextSecurity: "disc" } as React.CSSProperties)}
            className={cn(
              "border-border bg-background text-foreground h-12 w-full rounded-lg border py-2 pr-10 pl-10 text-center text-lg tracking-[0.3em] transition-colors outline-none",
              "focus:border-primary focus:ring-primary/20 focus:ring-2",
              error && "border-destructive focus:border-destructive focus:ring-destructive/20",
            )}
          />
          {verifying ? (
            <Loader2 className="text-primary absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin" />
          ) : (
            <button
              type="button"
              onClick={() => setShowPin(!showPin)}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
            >
              {showPin ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          )}
        </div>

        {error && (
          <m.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-destructive text-sm"
          >
            {error === "limited" ? (
              <LimitedMessage retryAfter={retryAfter} />
            ) : (
              <Trans>Wrong PIN</Trans>
            )}
          </m.p>
        )}

        {hasBiometric && (
          <button
            onClick={handleBiometric}
            disabled={biometricBusy}
            className="text-muted-foreground hover:text-foreground mx-auto flex min-h-11 items-center gap-2 px-3 text-sm transition-colors disabled:opacity-50"
          >
            {biometricBusy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Fingerprint className="size-4" />
            )}
            <Trans>Use biometric</Trans>
          </button>
        )}
      </div>

      <button
        onClick={onCancel}
        className="text-muted-foreground hover:text-foreground min-h-11 px-3 text-sm transition-colors"
      >
        <Trans>Go back</Trans>
      </button>
    </div>
  );
}

/** "Too many attempts" with the server's wait — in minutes, or hours past one. */
function LimitedMessage({ retryAfter }: { retryAfter: number | null }) {
  if (retryAfter === null) {
    return <Trans>Too many attempts. Wait a few minutes, then try again.</Trans>;
  }
  if (retryAfter >= 60 * 60) {
    const hours = Math.ceil(retryAfter / 3600);
    return <Trans>Too many wrong PINs. PIN entry is paused — try again in {hours} h.</Trans>;
  }
  const minutes = Math.max(1, Math.ceil(retryAfter / 60));
  return <Trans>Too many wrong PINs. PIN entry is paused — try again in {minutes} min.</Trans>;
}

export function PinGateModal({
  open,
  onUnlock,
  onCancel,
  pinLength,
  hasBiometric,
}: PinGateModalProps) {
  const { t } = useLingui();

  return (
    <ResponsiveSheet
      open={open}
      onClose={onCancel}
      width="sm"
      hideHandle
      hideCloseButton
      // Consumer's onCancel already calls window.history.back() to leave the
      // protected route. The sheet's default sentinel + popstate handling
      // causes a double-back race (cleanup pops the sentinel asynchronously
      // while history.back() is still queued), so the user ends up two
      // entries back instead of one.
      disableHistory
      ariaLabel={t`Unlock medical vault`}
    >
      {open && (
        <PinGateForm
          onUnlock={onUnlock}
          onCancel={onCancel}
          pinLength={pinLength}
          hasBiometric={hasBiometric}
        />
      )}
    </ResponsiveSheet>
  );
}
